import React, { useCallback, useRef, useState } from 'react';
import Autocomplete, { AutocompleteInputChangeReason } from '@material-ui/lab/Autocomplete';
import uniqueId from 'lodash-es/uniqueId';
import isEqual from 'react-fast-compare';
import { makeStyles } from '@material-ui/core/styles';
import { PopperProps } from '@material-ui/core/Popper';
import styled from 'styled-components';
import { SystemIcon } from '../Icon/SystemIcon';
import { useDebounce } from '../../hooks/useDebounce';
import { Label } from '../Form/Label';
import Typography from '../Typography/Typography';
import { getHelperText, HelperText } from '../Form/HelperText';
import { useEffectOnce } from '../../hooks';
import { tokens } from '../../theme';
import { useIntlUds } from '../../translations/UdsIntlProvider';
import { AutocompleteInput } from './MuiSelectElements/elements/AutocompleteInput';
import { ListboxComponent } from './MuiSelectElements/MuiSelectAutocompleteVirtualComponents';
import { Option } from './MuiSelectElements/elements/Option';
import {
  CreatableOption,
  InternalAutocompleteRenderInputParams,
  MuiMultiSelectProps,
  MuiSelectProps,
  OptionTypeWithCode
} from './types';
import { useForceCloseEvent } from './MuiSelectElements/utils/useForceCloseEvent';
import { StyledFormControl } from './MuiSelectElements/elements/StyledFormControl';
import { PaperMuiComponent } from './MuiSelectElements/elements/PaperMuiComponent';
import { PopperAdapter } from './MuiSelectElements/elements/PopperAdapter';
import { useDropdownOptions } from './MuiSelectElements/utils/useDropdownOptions';
import { useFilterHandler } from './MuiSelectElements/utils/useFilterHandler';
import { useChangeHandler } from './MuiSelectElements/utils/useChangeHandler';
import { Chips } from './MuiSelectElements/elements/Chips';
import { Group } from './MuiSelectElements/elements/Group';
import { isCreatableOption } from './MuiSelectElements/utils/guards';
import { LabelWithIcon } from '../Form/LabelWithIcon';
import { MAX_TERM_LENGTH } from '../Embolden';

const EMPTY: OptionTypeWithCode[] = [];

export { FORCE_CLOSE_EVENT } from './MuiSelectElements/utils/useForceCloseEvent';

export function MuiSelect<VALUE = string, MULTI extends boolean = false>({
  // basic
  name,
  id,
  style,
  className,
  description,
  label,
  placeholder,
  autoComplete,
  disabled,
  disableClearable,
  error,
  required,
  fullWidth = true,
  limitChips,
  dataTest = 'select',
  loading,
  debounce = 300,
  helperText,
  errorText,
  tooltipText,
  searchMode,
  visibleElementsWhenSelected = { label: true, code: true },

  // values & options
  options,
  uniqueOptions,
  value,
  valueOptions,

  // components props
  menuProps,
  listBoxProps,

  // custom components
  components,

  // handlers
  onOpen,
  onOptionChange,
  onChange,
  onSearch,
  onCreate,
  onBlur,

  ...rest // multiple, closeOnSelect
}: MuiSelectProps<MULTI, VALUE>) {
  const { intl, format } = useIntlUds();
  const newId = id ?? uniqueId('MuiSelect');
  const classes = useStyles();
  const [isFocused, setIsFocused] = useState(false);
  const { multiple, closeOnSelect } = (rest ?? {}) as unknown as MuiMultiSelectProps<VALUE>;
  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(menuProps?.openOnStart ?? false);
  const [willTriggerDebounceSearch, setWillTriggerDebounceSearch] = useState(false);
  const autocompleteRef = useRef<HTMLDivElement>();
  const isCreatable = !!onCreate;
  const [searchTerm, setSearchTerm] = useState('');
  const isLoading = loading || willTriggerDebounceSearch;
  const hasFormControl = !!label || !!description || !!helperText || !!errorText;
  const formControlProps = hasFormControl ? { required, error, fullWidth, dataTest: `${dataTest}-form-control` } : {};
  const usePortal = menuProps?.usePortal ?? true;
  useForceCloseEvent(setOpen);

  const dropdownOptions = useDropdownOptions(
    value,
    valueOptions,
    options,
    menuProps?.sort,
    menuProps?.showUnmatchedValues ?? true,
    uniqueOptions
  );
  const handleOptionsChange = useChangeHandler<VALUE>(multiple, onChange, onOptionChange, onCreate);
  const handleFilterOptions = useFilterHandler<VALUE>(
    onSearch,
    menuProps?.createOptionPrefix ?? 'Add',
    isCreatable,
    loading
  );

  const handleSearch = useDebounce(
    onSearch
      ? (term: string) => {
          onSearch(term);
          setWillTriggerDebounceSearch(false);
        }
      : noop,
    debounce
  );

  const onInputChangeHandler = useCallback(
    (event: React.ChangeEvent<unknown>, value: string, reason: AutocompleteInputChangeReason) => {
      setSearchTerm(searchMode ? value.substring(0, MAX_TERM_LENGTH) : value);
      if (onSearch) {
        if (reason === 'input' || reason === 'clear' || (reason === 'reset' && value === '')) {
          setWillTriggerDebounceSearch(debounce !== 0);
          handleSearch(value);
        }
      }
    },
    [debounce, handleSearch, onSearch, searchMode]
  );

  const regainFocusOnChipClick = () => {
    autocompleteRef.current?.querySelector('input')?.focus();
  };

  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      setIsFocused(false);
      onBlur?.(e);
    },
    [onBlur]
  );

  /**
   * trigger onSearch on component initialization
   */
  useEffectOnce(() => {
    onSearch?.('');
  });
  const selectedOptions = multiple ? (dropdownOptions.selected ?? null) : (dropdownOptions.selected?.[0] ?? null);

  const Wrapper = hasFormControl ? StyledFormControl : React.Fragment;
  return (
    <Wrapper {...formControlProps}>
      {!!label && (
        <>
          {!tooltipText && (
            <Label required={required} shrink htmlFor={newId} dataTest={`${dataTest}-label`}>
              {label}
            </Label>
          )}
          {tooltipText && (
            <LabelWithIcon
              required={required}
              shrink
              htmlFor={newId}
              dataTest={`${dataTest}-label`}
              slotProps={{ tooltipProps: { title: tooltipText } }}
            >
              {label}
            </LabelWithIcon>
          )}
        </>
      )}

      {!!description && <StyledDescription variant="body2">{description}</StyledDescription>}
      <Autocomplete
        data-version="legacy"
        data-component-role="autocomplete"
        id={newId}
        fullWidth={hasFormControl ? true : fullWidth}
        noOptionsText={menuProps?.noOptionsText}
        options={isLoading && !menuProps?.showOptionsDuringLoading ? EMPTY : dropdownOptions.list}
        disabled={disabled}
        open={open}
        style={getStyle(style, error)}
        classes={{
          clearIndicator: isFocused ? classes.clearIndicatorDirty : classes.clearIndicator
        }}
        data-test={`${dataTest}-field`}
        className={className}
        disablePortal={!usePortal}
        onChange={(e, value, reason) => {
          // in this case it is React.ChangeEvent<HTMLInputElement> not React.ChangeEvent<{}>
          if (!(reason === 'clear' && (e as React.ChangeEvent)?.target?.tagName === 'INPUT')) {
            handleOptionsChange(value);
          }
          if (searchMode) {
            setSearchTerm('');
            onBlur?.(e as React.FocusEvent);
          }
        }}
        onOpen={(e) => {
          // in this case it is React.ChangeEvent<HTMLInputElement> not React.ChangeEvent<{}>
          if (
            disabled ||
            hasParent(e as React.ChangeEvent, 'uds-chip') ||
            hasParent(e as React.ChangeEvent, 'muiSelect-close-icon')
          ) {
            return;
          }
          onOpen?.(e);
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        onInputChange={onInputChangeHandler}
        loading={isLoading}
        inputValue={searchMode ? searchTerm : undefined}
        onFocus={() => setIsFocused(true)}
        onBlur={handleBlur}
        value={selectedOptions}
        selectOnFocus={isCreatable ? true : undefined}
        clearOnBlur={isCreatable ? true : undefined}
        freeSolo={isCreatable ? true : undefined}
        handleHomeEndKeys={isCreatable ? true : undefined}
        blurOnSelect={!!searchMode}
        disabledItemsFocusable={false}
        multiple={multiple}
        forcePopupIcon={components?.PopupIcon?.() !== null}
        closeIcon={
          components?.ClearIcon ? (
            components.ClearIcon()
          ) : (
            <SystemIcon name="close_small" dataTest="muiSelect-close-icon" />
          )
        }
        disableCloseOnSelect={multiple && !closeOnSelect}
        PopperComponent={
          components?.Popper ?? (PopperAdapter(menuProps?.placement) as React.ComponentType<PopperProps>)
        }
        PaperComponent={(paperProps) => (components?.Paper ?? PaperMuiComponent)({ ...menuProps, paperProps })}
        filterOptions={menuProps?.filterOptions ?? handleFilterOptions}
        ref={autocompleteRef}
        getOptionSelected={(option1, option2) => isEqual(option1.value, option2.value)}
        ListboxComponent={
          components?.MenuList ??
          (ListboxComponent as React.ComponentType<React.PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>>)
        }
        ListboxProps={listBoxProps}
        getOptionLabel={(option) => getOptionLabel(option)}
        renderGroup={components?.Group ?? Group}
        groupBy={menuProps?.group ? (option) => (option as OptionTypeWithCode)?.groupName ?? '' : undefined}
        popupIcon={components?.PopupIcon ? components.PopupIcon() : undefined}
        renderInput={(params) =>
          (components?.Input ?? AutocompleteInput)({
            params: params as InternalAutocompleteRenderInputParams,
            onFocus: setFocused,
            onClear: () => handleOptionsChange(null),
            selectedOptions,
            loading: isLoading,
            visibleElementsWhenSelected,
            placeholder,
            autoComplete,
            open,
            name
          })
        }
        renderTags={(selectedOptions: OptionTypeWithCode[], getTagProps) => {
          const isFocused = focused && !loading;
          const chipsLimit = limitChips ?? 999;
          const limit = isFocused ? 999 : chipsLimit;
          const endText = selectedOptions.length <= limit ? 0 : selectedOptions.length - limit;
          return (components?.Chips ?? Chips)({
            selectedOptions,
            getTagProps,
            visibleElementsWhenSelected,
            limitChips: chipsLimit,
            isFocused,
            onBeforeChipClick: regainFocusOnChipClick,
            endText: !!endText ? format(intl.Select.Chips.endText, { endText: endText.toString() }) : ''
          });
        }}
        renderOption={(option, state) =>
          (components?.Option ?? Option)({
            option,
            state,
            dataTest,
            searchTerm,
            noWrapSubLabel: menuProps?.noWrapSubLabel
          })
        }
        filterSelectedOptions={
          typeof menuProps?.hideSelectedOptions === 'boolean'
            ? menuProps.hideSelectedOptions
            : !!autoComplete || !!onSearch || !!multiple
        }
        disableClearable={disableClearable}
      />
      {(!!helperText || !!errorText) && (
        <HelperText error={error}>{getHelperText(error, helperText, errorText)}</HelperText>
      )}
    </Wrapper>
  );
}

function hasParent(e: { target?: Element } | undefined, dataTest: string): boolean {
  return !!e?.target?.closest(`[data-test="${dataTest}"]`);
}

function noop() {}

const useStyles = makeStyles({
  clearIndicator: {
    display: 'none'
  },
  clearIndicatorDirty: {
    display: 'initial'
  }
});

function getStyle(style?: React.CSSProperties, error?: boolean): React.CSSProperties | undefined {
  if (error) {
    return {
      ...style,
      borderColor: tokens.colorAlert
    };
  }
  return style;
}

const getOptionLabel = (option: OptionTypeWithCode | CreatableOption): string => {
  if (isCreatableOption(option)) {
    return `${option.creatableTitle}`;
  }
  return option.label ? `${option.label}` : '';
};

const StyledDescription = styled(Typography)`
  && {
    margin-bottom: 2px;
  }
`;
